import { useMutation, useQuery } from '@apollo/client';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../core/components/Alert.tsx';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import Table, { TableScrollWrapper } from '../../core/components/Table.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { formatFileSize, newestFilePerPattern } from '../files.ts';
import { GET_SFTP_DIRECTORY, IMPORT_LATEST_SFTP_FILES } from '../queries.ts';
import { SftpDirectory } from '../types.ts';

const RefreshIcon = withIcon(faArrowsRotate);

interface SftpImportCardProps {
    lookupDefinitionId: string;
    onImportStarted: () => void;
}

const SftpImportCard = ({ lookupDefinitionId, onImportStarted }: SftpImportCardProps) => {
    const { t } = useTranslation('masterdata');
    const { publishToast } = useToaster();
    const { data, loading, error, refetch } = useQuery<{ sftpDirectory: SftpDirectory }>(GET_SFTP_DIRECTORY, {
        variables: { lookupDefinitionId, path: null },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });
    const sftpDirectory = data?.sftpDirectory;
    const files = newestFilePerPattern(sftpDirectory?.entries ?? []);

    const [importLatestSftpFiles, { loading: isImporting }] = useMutation(IMPORT_LATEST_SFTP_FILES, {
        variables: { lookupDefinitionId },
        onCompleted: (result) => {
            const fileNames = result.importLatestSftpFiles.fileNames.join(', ');
            publishToast({ description: t('hub.detail.sftpImport.importSuccess', { fileNames }), status: 'success' });
            onImportStarted();
        },
        onError: (importError) => {
            console.error(importError);
            publishToast({
                description: t('hub.detail.sftpImport.importError', { error: importError.message }),
                status: 'error',
            });
        },
    });

    return (
        <SettingsCard
            title={t('hub.detail.sftpImport.title')}
            description={t('hub.detail.sftpImport.description')}
            controls={
                <Button
                    variant="ghost"
                    onClick={() => refetch()}
                    aria-label={t('hub.detail.sftpImport.refresh')}
                    title={t('hub.detail.sftpImport.refresh')}
                >
                    <RefreshIcon />
                </Button>
            }
        >
            {loading ? (
                <div className="text-sm text-content-medium">{t('hub.detail.sftpImport.loading')}</div>
            ) : error ? (
                <Alert severity="error">{error.message}</Alert>
            ) : files.length === 0 ? (
                <div className="text-sm text-content-medium">
                    {t('hub.detail.sftpImport.noMatchingFile', { path: sftpDirectory?.path })}
                </div>
            ) : (
                <TableScrollWrapper>
                    <Table className="w-full table-auto">
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell>{t('importConfig.sftp.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('importConfig.sftp.pattern')}</Table.HeadCell>
                                <Table.HeadCell>{t('importConfig.sftp.size')}</Table.HeadCell>
                                <Table.HeadCell>{t('importConfig.sftp.modified')}</Table.HeadCell>
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {files.map((file) => (
                                <Table.Row key={file.path}>
                                    <Table.Cell className="whitespace-nowrap">{file.name}</Table.Cell>
                                    <Table.Cell className="whitespace-nowrap font-mono">
                                        {file.matchingPattern}
                                    </Table.Cell>
                                    <Table.Cell className="whitespace-nowrap">
                                        {file.size !== null && formatFileSize(file.size)}
                                    </Table.Cell>
                                    <Table.Cell className="whitespace-nowrap">
                                        {file.modifiedAt && format(new Date(file.modifiedAt), 'PPp')}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableScrollWrapper>
            )}

            <div className="flex justify-end">
                <Button
                    type="button"
                    variant="primary"
                    onClick={() => importLatestSftpFiles()}
                    disabled={files.length === 0}
                    loading={isImporting}
                >
                    {t('hub.detail.sftpImport.importButton')}
                </Button>
            </div>
        </SettingsCard>
    );
};

export default SftpImportCard;
